import React from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { Cookie, ArrowRight } from "lucide-react";
import { openCookiePreferences } from "@/components/CookieBanner";

const BANNER = "https://images.unsplash.com/photo-1442550528053-c431ecb55509?auto=format&fit=crop&w=2000&q=80";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-serif-display font-bold mb-3" style={{ color: "#2a160c", fontSize: "clamp(1.2rem, 2vw, 1.6rem)" }}>
      {title}
    </h2>
    <div className="prose-cc">{children}</div>
  </section>
);

const CookiePolicy = () => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || "it").slice(0, 2);
  const localeMap = { it: "it-IT", en: "en-GB", ja: "ja-JP" };
  return (
    <PageShell>
      <SeoHead pageKey="cookie" image={BANNER} noIndex />
      <PageBanner
        eyebrow={t("cookiePage.banner.eyebrow")}
        title={t("cookiePage.banner.title")}
        subtitle={t("cookiePage.banner.subtitle")}
        image={BANNER}
        height="min(48vh, 420px)"
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl p-6 sm:p-8 mb-10" style={{ background: "#f5ead6", border: "1px solid rgba(91,54,33,0.15)" }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#c5371f" }}>
                <Cookie size={16} color="#fff" />
              </div>
              <div className="text-sm leading-relaxed" style={{ color: "#3b2417" }}>
                <p>
                  {t("cookiePage.intro")}{" "}
                  <Link to="/privacy-policy">{t("footer.privacy")}</Link>.
                </p>
                <p className="mt-2 text-xs opacity-80">
                  {t("cookiePage.updated")} {new Date().toLocaleDateString(localeMap[locale] || "it-IT")}
                </p>
              </div>
            </div>
          </div>

          <Section title={t("cookiePage.s1.title")}>
            <p>{t("cookiePage.s1.p1")}</p>
          </Section>

          <Section title={t("cookiePage.s2.title")}>
            <ul>
              <li dangerouslySetInnerHTML={{ __html: t("cookiePage.s2.li1") }} />
              <li dangerouslySetInnerHTML={{ __html: t("cookiePage.s2.li2") }} />
              <li dangerouslySetInnerHTML={{ __html: t("cookiePage.s2.li3") }} />
            </ul>
          </Section>

          <Section title={t("cookiePage.s3.title")}>
            <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid rgba(91,54,33,0.15)" }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: "#2a160c", color: "#f5ead6" }}>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs">{t("cookiePage.s3.th1")}</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs">{t("cookiePage.s3.th2")}</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs">{t("cookiePage.s3.th3")}</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-xs">{t("cookiePage.s3.th4")}</th>
                  </tr>
                </thead>
                <tbody style={{ background: "#f5ead6" }}>
                  <tr style={{ borderTop: "1px solid rgba(91,54,33,0.1)" }}>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: "#c5371f" }}>cc_cookie_consent_v1</td>
                    <td className="px-4 py-3" style={{ color: "#3b2417" }}>{t("cookiePage.s3.cookieType")}</td>
                    <td className="px-4 py-3" style={{ color: "#3b2417" }}>{t("cookiePage.s3.cookieDuration")}</td>
                    <td className="px-4 py-3" style={{ color: "#5b3621" }}>{t("cookiePage.s3.cookiePurpose")}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm" style={{ color: "#5b3621" }}>
              {t("cookiePage.s3.note")}
            </p>
          </Section>

          <Section title={t("cookiePage.s4.title")}>
            <p>{t("cookiePage.s4.p1")}</p>
            <div className="mt-5 not-prose">
              <button
                onClick={openCookiePreferences}
                className="btn-red inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold uppercase tracking-wider text-xs"
                data-testid="cookie-page-open-prefs"
              >
                {t("cookiePage.manageBtn")} <ArrowRight size={14} />
              </button>
            </div>
          </Section>

          <Section title={t("cookiePage.s5.title")}>
            <p>{t("cookiePage.s5.p1")}</p>
            <ul>
              <li>{t("cookiePage.s5.li1")}</li>
              <li>{t("cookiePage.s5.li2")}</li>
              <li>{t("cookiePage.s5.li3")}</li>
              <li>{t("cookiePage.s5.li4")}</li>
            </ul>
            <p>{t("cookiePage.s5.p2")}</p>
          </Section>

          <Section title={t("cookiePage.s6.title")}>
            <p>
              {t("cookiePage.s6.p1Prefix")} <a href="mailto:caffecola1960@libero.it">caffecola1960@libero.it</a>.
            </p>
          </Section>
        </div>
      </section>
    </PageShell>
  );
};

export default CookiePolicy;
